import React from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { EnrollmentDetailsModel, AjaxLeadFormModel, LinkModel, SiteSettingsModel } from '../../models/globals';
import { EnrollNowButton } from './EnrollNowButton';
import { CtpContactUsButton } from './CtpContactUsButton';
import CheckMarkIcon from '../../svgs/checkmark.svg';
import { InfoTooltip } from '../common/InfoTooltip';
import { AffirmCallout } from '../affirm/AffirmCallout';

import {
    getEnrollmentDateText,
    getFirstAvailableSchedule,
    getOptionSubText,
    getOptionText,
    getVoucherOptionText,
    hasCtpVoucherOptions,
    isCtpOrCtpBundle,
} from '../../utilities/ProductUtilities';

interface EnrollmentProps {
    changeSchoolLink: LinkModel;
    className?: string;
    products: EnrollmentDetailsModel[];
    partnerName: string;
    leadFormModel: AjaxLeadFormModel;
    siteSettings?: SiteSettingsModel;
}

interface DiscountInfoProps {
    partnerPrice: number | null;
    srp: number | null;
}

const WhyIsThisSchoolChosen =
    "The school has been chosen based on your location or if you've visited this school's website.";

const WhyThisSchoolText = 'Why this school?';

// There might be a better way, but this tooltip also works on mobile and with a screen reader
const WhyThisSchool: React.FC = () => (
    <InfoTooltip id="describe-why" label={WhyThisSchoolText} placement="top" tooltip={WhyIsThisSchoolChosen}>
        <span className="mr-3 e2g-enrollment_tooltip">{WhyThisSchoolText}</span>
    </InfoTooltip>
);

const DiscountInfo: React.FC<DiscountInfoProps> = ({ partnerPrice, srp }) => {
    if (srp && partnerPrice && srp > partnerPrice) {
        const discount = srp - partnerPrice;
        const percentDiscount = Math.ceil((discount / srp) * 100);
        const figureSpace = '\u2007';

        return (
            <>
                {figureSpace}
                <span className="e2g-enrollment_srp">{`${currencyFormat(srp)}`}</span>
                {figureSpace}
                {`${percentDiscount}% off`}
            </>
        );
    }

    return null;
};

const currencyFormat = (price: number | null) => {
    // We can safely assume that the value input is a non-zero positive value
    // The C# code that creates the model, sends in a NULL value when the value
    // is LTE zero.
    if (!price) return '';

    const priceString = price % 1 === 0 ? price.toString() : price.toFixed(2);

    const formattedPrice = priceString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return '$' + formattedPrice;
};

const checkedEnrollmentButtonDomId = 'checked-enrollment-btn';
const enrollmentSectionDomId = 'enrollment-section';
const getProductPriceInCents = (product: EnrollmentDetailsModel) => {
    const schedule = getFirstAvailableSchedule(product);
    const price = schedule?.price ?? product.partnerPrice;
    return Math.ceil(price || 0) * 100;
};

const Enrollment: React.FC<EnrollmentProps> = ({
    changeSchoolLink,
    className = '',
    partnerName,
    products,
    leadFormModel,
    siteSettings,
}) => {
    const [checkedOption, setCheckedOption] = React.useState(products[0].addToCartLink.url);

    return (
        <Row
            className={`${className} no-gutters enrollment bg-e2g_enrollment text-white`.trim()}
            id={enrollmentSectionDomId}
        >
            <Container>
                <Row>
                    <div className="col-lg-5 px-3 pt-5 pb-0">
                        <div className="text-uppercase">Offered in partnership with your preferred school</div>
                        <h2 className="h5 pt-2">{partnerName}</h2>
                        <div>
                            <WhyThisSchool />

                            <a className="e2g-enrollment_change-schools" href={changeSchoolLink.url}>
                                {changeSchoolLink.name}
                            </a>
                        </div>
                    </div>
                    <div className="col-lg-7 px-3 py-5">
                        <div className="mb-5 border-bottom border-white d-md-none border-bottom border-white" />
                        <div className="text-uppercase">
                            {hasCtpVoucherOptions(products) ? 'Bundle and save' : 'Learning method'}
                        </div>
                        {siteSettings?.enableAffirm && (
                            <AffirmCallout pageType="product" price={getProductPriceInCents(products[0])} />
                        )}

                        {products.map((product) => {
                            const {
                                addToCartLink,
                                partnerPrice,
                                suggestedRetailPrice,
                                isConversionEnabled,
                                isProductInCart,
                                addToCartGtmAnalyticsData,
                            } = product;
                            const checked = checkedOption === addToCartLink.url;
                            const enrollmentDateText = getEnrollmentDateText(product);
                            const optionText = hasCtpVoucherOptions(products)
                                ? getVoucherOptionText(product)
                                : getOptionText(product);
                            const schedule = getFirstAvailableSchedule(product);
                            const price = schedule?.price ?? partnerPrice;

                            return (
                                <label className="mt-2 w-100" htmlFor={addToCartLink.url} key={addToCartLink.url}>
                                    <div
                                        className={`${
                                            checked ? 'checked' : ''
                                        } e2g-enrollment_learning-method p-3`.trim()}
                                    >
                                        <div className="row px-2">
                                            <div className="col-sm-7">
                                                <div className="row">
                                                    {products.length > 1 && (
                                                        <div className="align-items-center col-1 d-flex justify-content-center px-3">
                                                            <input
                                                                aria-label={optionText}
                                                                checked={checked}
                                                                id={addToCartLink.url}
                                                                name="enrollment-option"
                                                                onChange={(e) => setCheckedOption(e.target.value)}
                                                                type="radio"
                                                                value={addToCartLink.url}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="col pl-2 pr-0">
                                                        <div className="font-weight-bold">{optionText}</div>

                                                        <div className="font-weight-bold">
                                                            {currencyFormat(price)}
                                                            <DiscountInfo
                                                                partnerPrice={price}
                                                                srp={suggestedRetailPrice}
                                                            />
                                                        </div>

                                                        <div className="small">{getOptionSubText(product)}</div>
                                                        {enrollmentDateText && (
                                                            <div className="small">{enrollmentDateText}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className={
                                                    'align-self-center col' + (isProductInCart ? '' : ' pt-3 pt-sm-0')
                                                }
                                            >
                                                {checked && isProductInCart && (
                                                    <div className="align-items-center col-sm d-flex e2g-enroll_btn justify-content-center p-0 text-white">
                                                        <CheckMarkIcon />
                                                        Product Added
                                                    </div>
                                                )}
                                                <div className="align-items-center col-sm d-flex e2g-enroll_btn justify-content-center p-0">
                                                    {checked && (
                                                        <EnrollNowButton
                                                            addToCartGtmAnalyticsData={addToCartGtmAnalyticsData}
                                                            addToCartLink={addToCartLink}
                                                            showButton={isConversionEnabled}
                                                            id={checkedEnrollmentButtonDomId}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </label>
                            );
                        })}

                        {isCtpOrCtpBundle(products[0]) && (
                            <div className="pt-4">
                                <span className="e2g-contact_inner">
                                    <CtpContactUsButton leadFormModel={leadFormModel} />
                                </span>

                                <span className="pl-2 e2g-contact_inner"> for additional information</span>
                            </div>
                        )}
                    </div>
                </Row>
            </Container>
        </Row>
    );
};

export { Enrollment, EnrollmentProps, WhyIsThisSchoolChosen, checkedEnrollmentButtonDomId, enrollmentSectionDomId };
